import type { Node as PMNode } from '@tiptap/pm/model';
import { formatCurrency } from '@/lib/format';

/**
 * Kolommen per pricing type — gelijk aan InvestmentTotalView.handleAddRow.
 */
export function colCountForPricingType(pricingType: string): number {
  if (pricingType === 'hourly') return 4;
  if (pricingType === 'fixed') return 1;
  return 2;
}

export function rowHasHeaderCell(rowNode: PMNode): boolean {
  if (rowNode.type.name !== 'tableRow') return false;
  let found = false;
  rowNode.forEach((cell: PMNode) => {
    if (cell.type.name === 'tableHeader') found = true;
  });
  return found;
}

/**
 * Aantal tableCell-kolommen in de eerste datarij (niet de kopregel).
 * Gebruikt als bron van waarheid als attrs ontbreken of afwijken.
 */
export function firstDataRowCellCount(tableNode: PMNode): number | null {
  for (let i = 0; i < tableNode.childCount; i++) {
    const row = tableNode.child(i);
    if (row.type.name !== 'tableRow') continue;
    if (rowHasHeaderCell(row)) continue;
    let n = 0;
    row.forEach((cell: PMNode) => {
      if (cell.type.name === 'tableCell') n++;
    });
    return n;
  }
  return null;
}

export function getTableChildIndexInGroup(groupNode: PMNode, tableNode: PMNode): number {
  for (let i = 0; i < groupNode.childCount; i++) {
    if (groupNode.child(i) === tableNode) return i;
  }
  return -1;
}

function paragraph(text?: string) {
  return text
    ? { type: 'paragraph' as const, content: [{ type: 'text' as const, text }] }
    : { type: 'paragraph' as const };
}

function tableCell(text?: string) {
  return {
    type: 'tableCell' as const,
    content: [paragraph(text)],
  };
}

/** Datarij JSON met defaults voor insertContentAt (TipTap / ProseMirror). */
export function emptyTableRowForPricing(pricingType: string, currency = 'EUR') {
  if (pricingType === 'hourly') {
    return {
      type: 'tableRow' as const,
      content: [
        tableCell(),
        tableCell(),
        tableCell('8'),
        tableCell(),
      ],
    };
  }

  if (pricingType === 'fixed') {
    return { type: 'tableRow' as const, content: [tableCell()] };
  }

  return {
    type: 'tableRow' as const,
    content: [tableCell(), tableCell(formatCurrency(0, currency))],
  };
}

/**
 * Nieuwe rij met hetzelfde aantal kolommen als de bestaande tabel (eerste datarij),
 * met fallback naar pricingType als er geen datarij te meten valt.
 */
export function emptyTableRowMatchingTable(tableNode: PMNode, pricingType: string, currency = 'EUR') {
  const fromTable = firstDataRowCellCount(tableNode);
  const expectedColCount = colCountForPricingType(pricingType);
  if (fromTable == null || fromTable === expectedColCount) {
    return emptyTableRowForPricing(pricingType, currency);
  }

  const cells = Array.from({ length: fromTable }, () => tableCell());
  return { type: 'tableRow' as const, content: cells };
}

/**
 * Bepaalt pricingType voor een tabel binnen investmentGroupNode.
 * Preferentie: attrs op de investmentTotalNode direct na de tabel; anders de voorafgaande investmentHeaderNode.
 */
export function getPricingTypeForTableInGroup(groupNode: PMNode, tableChildIndex: number): string {
  if (tableChildIndex + 1 < groupNode.childCount) {
    const next = groupNode.child(tableChildIndex + 1);
    if (next.type.name === 'investmentTotalNode') {
      const pt = (next.attrs as { pricingType?: string }).pricingType;
      if (pt) return pt;
    }
  }
  for (let i = tableChildIndex - 1; i >= 0; i--) {
    const c = groupNode.child(i);
    if (c.type.name === 'investmentHeaderNode') {
      const pt = (c.attrs as { pricingType?: string }).pricingType;
      if (pt) return pt;
    }
  }
  return 'fixed';
}
